import { TinyColor } from '@ctrl/tinycolor';
import {
  Button,
  Card,
  DatePicker,
  Dropdown,
  Flex,
  Form,
  Input,
  Switch,
  Tag,
  theme,
  TreeSelect,
  type TreeSelectProps,
  Typography,
} from 'antd';
import { ChevronDown } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { catchError, concatMap, EMPTY, of, tap } from 'rxjs';
import DetailedSite from '../../../components/detailted-site';
import SyncSuspense from '../../../components/sync-suspense';
import { type Agency, type Customer, CycleMenu, Site } from '../../../entities';
import { useNotification } from '../../../hooks/use-notification';
import {
  AtomicOperation,
  type AtomicResult,
  useAtomicOperations,
  useQuery,
} from '../../../jsonapi-provider';
import { LocalDate } from '../../../utils/local-date';
import utils from '../../../utils/utils';
import type dayjs from 'dayjs';

const SUCCESS_ADD_MESSAGE = 'Cycle menu added successfully';
const SUCCESS_UPDATE_MESSAGE = 'Cycle menu updated successfully';
const SUCCESS_CLONE_MESSAGE = 'Cycle menu cloned successfully';

interface CycleMenuFormProps {
  isClone?: boolean;
  cycleMenuToBeClonedId?: string;
  cycleMenu: CycleMenu;
  onSuccess: (cycleMenu: CycleMenu, isProgressive?: boolean) => void;
  onCancel: () => void;
  setCloningInProgress?: (inProgress: boolean) => void;
  cloningInProgress?: boolean;
}

type CycleMenuNode = CycleMenu & { children: CycleMenu[] };
const submitButtonActionText = (isClone: boolean = false): string => (isClone && 'Clone') || 'Save';

const buildSitesTree = (
  sites: Array<Site & { disableLabel?: string }>,

  setSiteElementParent: (value: Record<string, string>) => void
): TreeSelectProps['treeData'] => {
  const siteElementParent: Record<string, string> = {};
  const tree = Object.values(
    utils.groupBy(
      sites.filter((site) => site.agency && site.state && site.county && site.customer),
      ({ agency }) => agency.id!
    )
  ).map((sitesByAgency) => {
    return {
      value: `A@${sitesByAgency[0].agency.id}`,
      title: sitesByAgency[0].agency.name,
      disabled: sitesByAgency.some((site) => site.disableLabel),
      children: Object.values(
        utils.groupBy(sitesByAgency, ({ state, county }) => `${state}/${county}`)
      )
        .map((sitesByCounty) => {
          siteElementParent[
            `S@${sitesByAgency[0].agency.id}/${sitesByCounty[0].state}/${sitesByCounty[0].county}`
          ] = `A@${sitesByAgency[0].agency.id}`;
          return {
            value: `S@${sitesByAgency[0].agency.id}/${sitesByCounty[0].state}/${sitesByCounty[0].county}`,
            title: `${sitesByCounty[0].county}, ${sitesByCounty[0].state}`,
            disabled: sitesByCounty.some((site) => site.disableLabel),
            children: Object.values(utils.groupBy(sitesByCounty, ({ customer }) => customer.id!))
              .map((sitesByCustomer) => {
                siteElementParent[
                  `C@${sitesByAgency[0].agency.id}/${sitesByCounty[0].state}/${sitesByCounty[0].county}/${sitesByCustomer[0].customer.id}`
                ] = `S@${sitesByAgency[0].agency.id}/${sitesByCounty[0].state}/${sitesByCounty[0].county}`;
                return {
                  value: `C@${sitesByAgency[0].agency.id}/${sitesByCounty[0].state}/${sitesByCounty[0].county}/${sitesByCustomer[0].customer.id}`,
                  title: `${sitesByCustomer[0].customer.name}`,
                  disabled: sitesByCustomer.some((site) => site.disableLabel),
                  children: sitesByCustomer
                    .toSorted((a, b) => a.name.localeCompare(b.name))
                    .map((site) => {
                      siteElementParent[
                        site.id!
                      ] = `C@${sitesByAgency[0].agency.id}/${sitesByCounty[0].state}/${sitesByCounty[0].county}/${sitesByCustomer[0].customer.id}`;
                      return {
                        value: site.id,
                        title: (
                          <>
                            <DetailedSite
                              disabled={!!site.disableLabel}
                              site={site}
                              showCounty={false}
                            />
                            {!!site.disableLabel && (
                              <Tag style={{ marginInlineStart: 20, opacity: 0.5 }}>
                                {site.disableLabel}
                              </Tag>
                            )}
                          </>
                        ),
                        disabled: !!site.disableLabel,
                      };
                    }),
                };
              })
              .toSorted((a, b) => a.title.localeCompare(b.title)),
          };
        })
        .toSorted((a, b) => a.title.localeCompare(b.title)),
    };
  });
  setSiteElementParent(siteElementParent);
  return tree;
};
const addParentsWithSites = (
  sitesIds: string[],
  siteElementParent: Record<string, string>
): string[] => {
  const allIds = new Set(sitesIds);

  const parentChildrenMap: Record<string, string[]> = {};

  Object.entries(siteElementParent).forEach(([child, parent]) => {
    if (!parentChildrenMap[parent]) {
      parentChildrenMap[parent] = [];
    }

    parentChildrenMap[parent].push(child);
  });

  let changed = true;

  while (changed) {
    changed = false;

    Object.entries(parentChildrenMap).forEach(([parentId, children]) => {
      const selectedChildrenCount = children.filter((child) => allIds.has(child)).length;

      if (selectedChildrenCount === children.length && !allIds.has(parentId)) {
        allIds.add(parentId);
        changed = true;
      }
    });
  }

  return Array.from(allIds);
};

const CycleMenuForm = (props: CycleMenuFormProps): ReactNode => {
  const { token } = theme.useToken();
  const notification = useNotification();
  const [formInstance] = Form.useForm();
  const {
    isClone = false,
    cycleMenu,
    onSuccess,
    onCancel,
    cycleMenuToBeClonedId,
    setCloningInProgress,
    cloningInProgress,
  } = props;
  const isProgressiveSubmit = useRef(false);
  const [siteElementParent, setSiteElementParent] = useState<Record<string, string>>({});
  const isEditMode = useMemo(() => !!cycleMenu.id, [cycleMenu.id]);
  const initialValues = useMemo(
    () => ({
      ...cycleMenu,
      dateRange: [cycleMenu.startDate, cycleMenu.endDate],
      repeatUntil: cycleMenu.repeatUntil,
      sitesIds: addParentsWithSites(
        cycleMenu.sites.map((site) => site.id!),
        siteElementParent
      ),
    }),
    [cycleMenu]
  );
  useEffect(() => {
    formInstance.setFieldValue(
      'sitesIds',
      addParentsWithSites(
        cycleMenu.sites.map((site) => site.id!),
        siteElementParent
      )
    );
  }, [cycleMenu, siteElementParent]);

  const agencies = useQuery<Agency[]>([
    'agency',
    {
      sort: 'name',
      fields: { agency: ['name'] },
      page: {
        size: 10000,
      },
    },
  ]);
  const customers = useQuery<Customer[]>([
    'customer',
    {
      filter: 'isInactive==false',
      include: ['sites.agency'],
      sort: 'name',
      fields: {
        customer: ['name', 'sites'],
        site: [
          'name',
          'state',
          'county',
          'agency',
          'siteNumber',
          'custSiteNumber',
          'displaySiteNumber',
        ],
        agency: ['name'],
      },
      page: {
        size: 10000,
      },
    },
  ]);
  const cycleMenus = useQuery<CycleMenu[]>([
    'cycle-menu',
    {
      include: ['sites.customer', 'sites.agency'],
      fields: {
        'cycle-menu': ['name', 'sites', 'startDate', 'endDate', 'repeatUntil', 'isEnabled'],
        customer: ['name'],
        site: [
          'name',
          'customer',
          'state',
          'county',
          'agency',
          'siteNumber',
          'custSiteNumber',
          'displaySiteNumber',
        ],
        agency: ['name'],
      },
      page: {
        size: 10000,
      },
    },
  ]);
  const { operate, isLoading: isSubmitting } = useAtomicOperations();
  const allSites: Site[] = useMemo(
    () =>
      customers.data
        ?.map((cust) =>
          cust.sites.map((site) => Object.assign(new Site(), site, { customer: cust }))
        )
        .flat() ?? [],
    [customers.data]
  );

  const cycleMenusTree = useMemo(() => {
    const idMapping: Record<string, number> = {};
    const parents: CycleMenuNode[] = (cycleMenus.data ?? []).map((cm, ind) => {
      idMapping[cm.id!] = ind;
      return Object.assign(new CycleMenu(), cm, {
        children: [],
      });
    });

    return parents;
  }, [cycleMenus.data, customers.data]);

  const selectedDateRange: [LocalDate, LocalDate, LocalDate | null] = Form.useWatch(
    (values) => [
      values.dateRange[0],
      values.dateRange[1],
      values.repeatUntil ? values.repeatUntil : null,
    ],
    {
      form: formInstance,
      preserve: true,
    }
  );

  const getUnavailableSitesInDateRange = (
    startDate?: LocalDate,
    endDate?: LocalDate
  ): Array<{ id: Site['id']; disableLabel: string }> => {
    if (!startDate || !endDate) return [];
    return cycleMenusTree
      .filter((cm) => {
        const dr = cm.getDateRange();

        return (
          cm.id !== cycleMenu.id &&
          ((dr[0].isSameOrBefore(endDate) && dr[1].isSameOrAfter(startDate)) ||
            (dr[0].isSameOrAfter(startDate) && dr[0].isSameOrBefore(endDate)) ||
            (dr[1].isSameOrAfter(startDate) && dr[1].isSameOrBefore(endDate)))
        );
      })
      .map((cm: CycleMenu) =>
        cm.sites.map((site) => ({ id: site.id, disableLabel: `Assigned to ${cm.name}` }))
      )
      .flat();
  };

  const sitesTreeData = useMemo<TreeSelectProps['treeData']>(() => {
    const disabledSites: Array<{ id: Site['id']; disableLabel: string }> =
      getUnavailableSitesInDateRange(
        selectedDateRange?.[0],
        selectedDateRange?.[2] ?? selectedDateRange?.[1]
      );

    const possibleSitesObj = allSites.reduce<Record<string, Site & { disableLabel?: string }>>(
      (acc, cur) => ({ ...acc, [cur.id!]: { ...cur, disableLabel: undefined } }),
      {}
    );
    disabledSites.forEach(({ id, disableLabel }) => {
      if (possibleSitesObj[id!]) {
        possibleSitesObj[id!].disableLabel = disableLabel;
      }
    });

    return buildSitesTree(Object.values(possibleSitesObj).flat(), setSiteElementParent);
  }, [selectedDateRange, allSites, cycleMenus.data, cycleMenu]);

  useEffect(() => {
    formInstance.resetFields();
    formInstance.setFieldsValue(initialValues);
  }, [initialValues]);

  const getExclusiveIdsInTree = (ids: string[]): string[] => {
    return ids.filter((id) => {
      return !(id.startsWith('A@') || id.startsWith('S@') || id.startsWith('C@'));
    });
  };
  const onSubmit = async (
    submitted: CycleMenu & {
      dateRange: [LocalDate, LocalDate];
      repeatUntil?: LocalDate;
      sitesIds: string[];
    }
  ): Promise<void> => {
    const sitesIds = getExclusiveIdsInTree(submitted.sitesIds);

    const cycleMenuToBeSaved = new CycleMenu(
      cycleMenu.id,
      submitted.name,
      submitted.dateRange[0],
      submitted.dateRange[1],
      submitted.repeatUntil,
      submitted.isEnabled,
      sitesIds.map((id) => new Site(id))
    );

    // @ts-expect-error delete property
    delete cycleMenuToBeSaved.scheduledMeals;
    // @ts-expect-error delete property
    delete cycleMenuToBeSaved.cycleMenuBeverages;
    // @ts-expect-error delete property
    delete cycleMenuToBeSaved.cycleMenuSupplies;
    // @ts-expect-error delete property
    delete cycleMenuToBeSaved.sitesGroups;
    // @ts-expect-error delete property
    delete cycleMenuToBeSaved.agencies;
    const lid = isEditMode ? undefined : utils.randomUUID();

    if (isClone && cycleMenuToBeClonedId) {
      setCloningInProgress?.(true);
      cycleMenuToBeSaved.clonedCycleMenuId = cycleMenuToBeClonedId;
    }

    const cycleMenuOperation = isEditMode
      ? AtomicOperation.update(cycleMenuToBeSaved)
      : AtomicOperation.add(
          Object.assign(new CycleMenu(), cycleMenuToBeSaved, {
            ...cycleMenuToBeSaved,
            sitesGroups: undefined,
            agencies: undefined,
          }) as CycleMenu,
          lid
        );

    operate([cycleMenuOperation])
      .pipe(
        concatMap(([savedCycleMenu]: AtomicResult[]) => of(savedCycleMenu.data!)),
        tap((cm) => {
          notification.success({
            description: isClone
              ? SUCCESS_CLONE_MESSAGE
              : isEditMode
              ? SUCCESS_UPDATE_MESSAGE
              : SUCCESS_ADD_MESSAGE,
            message: '',
          });
          onSuccess(
            isEditMode ? cycleMenu : Object.assign(new CycleMenu(), cm),
            isProgressiveSubmit.current
          );
        }),
        tap(() => {
          isProgressiveSubmit.current = false;
        }),
        catchError((error: Error) => {
          const [description, title, ..._err] = error?.message.split('\n');
          notification.error({
            description: description ?? 'Something went wrong',
            message: title ?? '',
          });
          onCancel();
          return EMPTY;
        })
      )
      .subscribe();
  };

  const siteTagRender: TreeSelectProps['tagRender'] = ({ label, value }) => {
    const valueStr = value as string;
    if (formInstance.getFieldValue('sitesIds')?.includes(siteElementParent[valueStr])) return <></>;
    if (valueStr?.startsWith('A@'))
      return (
        <Tag color={token.colorPrimary}>
          {agencies.data?.find((agency) => agency.id === valueStr.split('@')[1])?.name ??
            'UNKNOWN AGENCY'}
        </Tag>
      );
    else if (valueStr?.startsWith('S@') || valueStr?.startsWith('C@')) {
      const valueSplit = valueStr.split(/@|\//);
      return (
        <Tag className="site-tag" color="processing">
          <Typography
            style={{
              background: token.colorPrimary,
              color: token.colorPrimaryText,
            }}
          >
            {agencies.data?.find((agency) => agency.id === valueSplit[1])?.name ?? 'UNKNOWN AGENCY'}
          </Typography>
          <Typography
            style={{
              background: new TinyColor(token.colorPrimary).setAlpha(0.3).toHslString(),
            }}
          >
            {valueSplit[3]}, {valueSplit[2]}
          </Typography>
          {valueSplit[4] ? (
            <Typography
              style={{
                background: new TinyColor(token.colorPrimary).setAlpha(0.1).toHslString(),
              }}
            >
              {customers.data?.find((customer) => customer.id === valueSplit[4])?.name ??
                'UNKNOWN CUSTOMER'}
            </Typography>
          ) : (
            ''
          )}
        </Tag>
      );
    } else return <Tag color="processing">{label}</Tag>;
  };

  return (
    <Flex vertical>
      <Card
        styles={{ body: { paddingBlock: 0, paddingInline: isClone ? 0 : undefined } }}
        style={{ borderWidth: isClone ? 0 : undefined, boxShadow: isClone ? 'none' : undefined }}
      >
        <Form
          form={formInstance}
          id="cycle-menu-form"
          initialValues={initialValues}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onFinish={onSubmit}
          layout="vertical"
          colon={false}
        >
          <SyncSuspense fallback="" condition={!isClone}>
            <Form.Item>
              <Typography.Title level={4}>
                {isEditMode ? cycleMenu.name : 'New Cycle Menu'}
              </Typography.Title>
            </Form.Item>
          </SyncSuspense>

          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Date range"
            name="dateRange"
            rules={[{ required: true, message: 'Date range is required' }]}
            getValueProps={(value: [LocalDate, LocalDate]) => ({
              value: value?.map((d) => d.toDayjs()),
            })}
            normalize={(value) => value?.map((d: dayjs.Dayjs) => new LocalDate(d))}
          >
            <DatePicker.RangePicker />
          </Form.Item>

          <Form.Item
            label="Repeat until"
            name="repeatUntil"
            dependencies={['dateRange']}
            getValueProps={(value: LocalDate) => ({ value: value?.toDayjs() })}
            normalize={(value) => value && new LocalDate(value)}
            rules={[
              { required: true, message: 'Repeat until is required' },
              ({ getFieldValue }) => ({
                async validator(_, value) {
                  const dateRange: [LocalDate, LocalDate] | undefined = getFieldValue('dateRange');
                  const endDate = dateRange?.[1];
                  if (!value || !endDate || !value.isBefore(endDate)) {
                    await Promise.resolve();
                    return;
                  }
                  return await Promise.reject(
                    new Error('Repeat until must be on or after the end date')
                  );
                },
              }),
            ]}
          >
            <DatePicker placeholder="No repeat" minDate={selectedDateRange?.[1]?.toDayjs()} />
          </Form.Item>

          <Form.Item label="Sites" name="sitesIds" dependencies={['dateRange', 'repeatUntil']}>
            <TreeSelect
              allowClear
              showSearch
              treeCheckable
              disabled={customers.isLoading || cloningInProgress}
              loading={customers.isLoading}
              showCheckedStrategy={TreeSelect.SHOW_ALL}
              treeNodeFilterProp="title"
              listHeight={500}
              treeDefaultExpandedKeys={agencies.data?.map((agency) => `A@${agency.id!}`) ?? []}
              treeData={sitesTreeData}
              tagRender={siteTagRender}
            />
          </Form.Item>

          <Form.Item label="Is enabled?" name="isEnabled" valuePropName="checked" hidden={isClone}>
            <Switch />
          </Form.Item>

          <Form.Item hidden={isClone}>
            <Flex gap="small" style={{ flexDirection: 'row-reverse' }}>
              <Dropdown
                menu={{
                  items: [
                    {
                      label: `${submitButtonActionText(isClone)}`,
                      key: '1',
                      onClick: () => {
                        formInstance.submit();
                      },
                    },
                    {
                      label: `${submitButtonActionText(isClone)} and edit scheduled meals`,
                      key: '2',
                      onClick: () => {
                        (isProgressiveSubmit.current = true) && formInstance.submit();
                      },
                    },
                  ],
                }}
              >
                <Button
                  block
                  icon={<ChevronDown />}
                  iconPosition="end"
                  type="primary"
                  disabled={customers.isLoading || cycleMenus.isLoading || isSubmitting}
                  loading={cycleMenus.isLoading || isSubmitting}
                >
                  {submitButtonActionText(isClone)}
                </Button>
              </Dropdown>
              <Button onClick={onCancel} block disabled={cycleMenus.isLoading || isSubmitting}>
                Discard
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </Card>
    </Flex>
  );
};

CycleMenuForm.defaultProps = {
  isClone: false,
};
export default CycleMenuForm;
